import type { FeatureConfiguration, IDcsgPz } from '@dsg-tech/pz-web-core';
import { useEffect, useState, useCallback } from 'react';

declare global {
  interface Window {
    dcsgPz: IDcsgPz;
  }
}

const FEATURE_CONFIG_EVENT = 'featureConfigChanged';

/**
 * Custom React hook to retrieve and subscribe to a feature configuration by key.
 *
 * This hook fetches the initial configuration for the specified feature key from the global
 * `window.dcsgPz.features` object and subscribes to configuration changes. When the configuration
 * changes and meets the criteria (`enabled` and `initialized`), the hook updates its internal state.
 * The subscription is automatically cleaned up when the component unmounts or the key changes.
 *
 * @param key - The unique identifier for the feature configuration to retrieve and subscribe to.
 * @returns The current `FeatureConfiguration` object if available and valid, otherwise `null`.
 */
const useFeatureConfiguration = (key: string): FeatureConfiguration | null => {
  const [config, setConfig] = useState<FeatureConfiguration | null>(null);

  // Memoize handler to avoid stale closure
  const handleConfigChange = useCallback(
    (newConfig: FeatureConfiguration) => {
      if (newConfig?.enabled && newConfig?.initialized) {
        window.dispatchEvent(new CustomEvent(FEATURE_CONFIG_EVENT, { detail: { key } }));
        setConfig(newConfig);
      }
    },
    [key],
  );

  useEffect(() => {
    const onFeatureConfigChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ key: string }>;
      if (customEvent.detail.key === key) {
        setConfig(window.dcsgPz?.features.getConfig(key));
      }
    };

    window.addEventListener(FEATURE_CONFIG_EVENT, onFeatureConfigChanged);

    const initialConfig = window.dcsgPz?.features.getConfig(key, handleConfigChange);
    if (initialConfig?.enabled && initialConfig?.initialized) {
      setConfig(initialConfig);
    }

    return () => {
      window.dcsgPz?.features.removeConfigChangeHandler(key, handleConfigChange);
      window.removeEventListener(FEATURE_CONFIG_EVENT, onFeatureConfigChanged);
    };
  }, [key, handleConfigChange]);

  return config;
};

export default useFeatureConfiguration;
